import { css } from '@emotion/react';
import { UnderlinedLinkComponent } from '~/_shared/baseComponents/links';
import { SUBSCRIBE_ROUTE } from '~/_shared/constants/routes';
import type { Theme } from '~/_shared/themes/theme.model';
import { isPaidLicense } from '~/_shared/types/client/license.helpers';
import { MapPrivacyLevel } from '~/_shared/types/map';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { openUrlInNewTab } from '~/_shared/utils/link/link';
import { isInfoOfBaseLayer } from '~/map/listing/mapListing.helpers';
import { useModal } from '~/modal/useModal.hook';
import {
  useMapInfoDataSelector, useMapInfoPrivacySelector,
} from '~/store/mapInfo/mapInfo.selectors';
import {
  useUserCurrentClientSelector, useUserDataUsagesSelector, useUserLimitsSelector,
} from '~/store/userData/userData.selectors';

const warningContentStyle = (theme: Theme) => css({
  padding: '0 10px 10px',
  color: theme.textColors.warning,
  fontSize: 12,
});

const infoContentStyle = (theme: Theme) => css({
  padding: '0 10px 10px',
  color: theme.textColors.info,
  fontSize: 12,
});

const descriptionStyle = css({
  margin: 0,
});

const ctaStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 4,
});

export const SharedMapProximityToolInfoComponent = () => {
  const currentClient = useUserCurrentClientSelector();
  const mapPrivacy = useMapInfoPrivacySelector();
  const limits = useUserLimitsSelector();
  const usages = useUserDataUsagesSelector();
  const mapInfo = useMapInfoDataSelector();
  const { replaceCurrentModal: openShareMapModal } = useModal('MapPrivacySelector');
  const [t] = useTranslation();

  const onSetPasswordClick = () => {
    if (!mapInfo || !mapPrivacy) {
      return;
    }

    const mapName = mapInfo.name;
    const isSnapshot = !!mapInfo.parentMap && !isInfoOfBaseLayer(mapInfo);

    openShareMapModal({
      isSnapshot,
      mapName,
      mapPrivacyInfo: {
        ...mapPrivacy,
        mapPrivacyLevel: MapPrivacyLevel.PasswordProtected,
        preservePasswordDisabledReason: t('There is no existing password set for the map.'),
      },
      skipDisabledTooltipShowTimeout: true,
      onSuccess: noop,
    });
  };

  if (!isPaidLicense(currentClient?.license.type)) {
    return (
      <div css={warningContentStyle}>
        <p css={descriptionStyle}>{t('mapSettings.dtpPublicInsufficientPlanWarning')}</p>

        <div css={ctaStyle}>
          <UnderlinedLinkComponent
            onClick={() => {
              openUrlInNewTab(SUBSCRIBE_ROUTE);
            }}
          >
            {t('Upgrade Subscription')}
          </UnderlinedLinkComponent>
        </div>
      </div>
    );
  }

  if (mapPrivacy?.mapPrivacyLevel !== MapPrivacyLevel.PasswordProtected) {
    return (
      <div css={warningContentStyle}>
        <p css={descriptionStyle}>{t('mapSettings.dtpPublicMissingPasswordWarning')}</p>

        <div css={ctaStyle}>
          <UnderlinedLinkComponent
            onClick={onSetPasswordClick}
          >
            {t('Set Password')}
          </UnderlinedLinkComponent>
        </div>
      </div>
    );
  }

  return (
    <div css={infoContentStyle}>
      <p css={descriptionStyle}>{t('mapSettings.dtpPublicCountInfo')}</p>

      <div css={ctaStyle}>
        <strong>
          {t('mapSettings.dtpPublicMapsUsage', {
            usage: usages.travelTimeRequests,
            limit: limits.travelTimeRequests,
          })}
        </strong>
      </div>
    </div>
  );
};
